import React, { useState } from "react";
import { Popover, PopoverSurface } from "@fluentui/react-components";
import moment from "moment";
import HoverCard from "../../../components/hoverCard/HoverCard";
import { Avatar } from "@fluentui/react-components";
import { TimelineTaskIcons } from "../../../helpers/utils";
import styles from "../ResourcesPlanner.module.scss";

/**
 * Universal allocation popover for resource / project / scope / task pills.
 *
 * Props:
 *  - level: "resource" | "project" | "scope" | "taskGroup" | "task"
 *  - resource: resource object (always present)
 *  - project: project object (for project/scope/taskGroup/task)
 *  - scope: scope object (for scope/taskGroup/task)
 *  - group: task group object (for taskGroup)
 *  - task: task object (for task)
 *  - data: level-specific computed data (see PlannerLeftPanel)
 *  - children: the pill element
 */
export default function ResourceAllocationPopover({ children, level, resource, project, scope, group, task, data }) {
  const [target, setTarget] = useState(null);
  const [open, setOpen] = useState(false);

  const updateCursorPosition = (e) => {
    const x = e.clientX + 10;
    const y = e.clientY + 10;
    setTarget({
      getBoundingClientRect: () => ({ x, y, top: y, left: x, right: x, bottom: y, width: 0, height: 0 }),
    });
  };

  const renderContent = () => {
    switch (level) {
      case "resource":
        return renderResource();
      case "project":
        return renderProject();
      case "scope":
        return renderScope();
      case "taskGroup":
        return renderTaskGroup();
      case "task":
        return renderTask();
      default:
        return null;
    }
  };

  // ── Resource ───────────────────────────────────────────────────────
  const renderResource = () => {
    const {
      resourceWeekly, totalAssignmentWeekly, isOverAssigned, overrun,
      allocated, assignmentCap, resourceCap,
    } = data;
    const isOverAllocated = allocated > resourceCap;
    const utilizationPct = assignmentCap > 0 ? Math.round((allocated / assignmentCap) * 100) : 0;

    return (
      <>
        {/* HoverCard as title */}
        <div style={{ marginBottom: 8 }}>
          <HoverCard
            HoverCardTrigger={
              <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "default" }}>
                <Avatar
                  size={24}
                  color="colorful"
                  name={resource?.name}
                  image={{ src: resource?.image || null }}
                />
                <span className={styles.summaryPopoverTitle}>{resource?.name}</span>
              </div>
            }
            AvatarId={resource?.id}
            AvatarName={resource?.name}
            AvatarImage={resource?.image}
            AvatarDesignation={resource?.title}
            AvatarEmail={resource?.email}
            AvatarPhone={resource?.phoneNumber}
            AvatarIsExternal={resource?.isExternal}
            CanSendMessage={true}
          />
        </div>
        <div className={styles.summaryPopoverDivider} />

        <Row label="Capacity" value={`${resourceWeekly} hrs/wk`} />
        <Row label="Total Assigned" value={`${totalAssignmentWeekly} hrs/wk`} isOver={isOverAssigned} />
        {isOverAssigned && (
          <Row label="Assignment Overrun" value={`+${overrun} hrs/wk`} isOver />
        )}

        <div className={styles.summaryPopoverDivider} />

        <Row label="Allocated" value={`${allocated} / ${assignmentCap} hrs`} isOver={isOverAllocated} />
        <Row label="Utilization" value={`${utilizationPct}%`} isOver={isOverAllocated} />

        <div className={styles.utilizationBarTrack}>
          <div
            className={`${styles.utilizationBarFill} ${(isOverAllocated) ? styles.utilizationBarOver : ""}`}
            style={{ width: `${Math.min(utilizationPct, 100)}%` }}
          />
        </div>

        {/* Per-project breakdown */}
        {resource?.projects?.length > 0 && (
          <>
            <div className={styles.summaryPopoverDivider} />
            <div className={styles.summaryPopoverLabel} style={{ marginBottom: 4 }}>Projects</div>
            {resource.projects.map((p) => (
              <Row
                key={p.id}
                label={p.name}
                value={`${p.maxWeeklyHours ?? 0} hrs/wk`}
                isOver={(p.maxWeeklyHours ?? 0) > resourceWeekly}
                truncateLabel
              />
            ))}
          </>
        )}
      </>
    );
  };

  // ── Project ────────────────────────────────────────────────────────
  const renderProject = () => {
    const {
      projAllocated, projCap, isProjectOverAssigned,
      projectMaxWeekly, resourceMaxWeekly, scopes,
    } = data;
    const isOverAllocated = projAllocated > projCap;
    const utilizationPct = projCap > 0 ? Math.round((projAllocated / projCap) * 100) : 0;

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div className="flex-shrink-0">{TimelineTaskIcons[project?.status]?.()}</div>
          <span className={styles.summaryPopoverTitle}>{project?.name}</span>
        </div>
        <div className={styles.summaryPopoverDivider} />

        <Row label="Assignment" value={`${projectMaxWeekly} hrs/wk`} isOver={isProjectOverAssigned} />
        <Row label="Resource Capacity" value={`${resourceMaxWeekly} hrs/wk`} />

        <div className={styles.summaryPopoverDivider} />

        <Row label="Allocated" value={`${projAllocated} / ${projCap} hrs`} isOver={isOverAllocated} />
        <Row label="Utilization" value={`${utilizationPct}%`} isOver={isOverAllocated} />

        <div className={styles.utilizationBarTrack}>
          <div
            className={`${styles.utilizationBarFill} ${(isOverAllocated) ? styles.utilizationBarOver : ""}`}
            style={{ width: `${Math.min(utilizationPct, 100)}%` }}
          />
        </div>

        {/* Per-scope breakdown */}
        {scopes?.length > 0 && (
          <>
            <div className={styles.summaryPopoverDivider} />
            <div className={styles.summaryPopoverLabel} style={{ marginBottom: 4 }}>Scopes</div>
            {scopes.map((s) => (
              <Row
                key={s.id}
                label={s.name}
                value={`${s.allocated ?? 0} hrs`}
                truncateLabel
              />
            ))}
          </>
        )}
      </>
    );
  };

  // ── Scope ──────────────────────────────────────────────────────────
  const renderScope = () => {
    const { scopeAllocated, tasks, projectMaxWeekly, projCap } = data;

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div className="flex-shrink-0">{TimelineTaskIcons[scope?.status]?.()}</div>
          <span className={styles.summaryPopoverTitle}>{scope?.name}</span>
        </div>
        <div className={styles.summaryPopoverDivider} />

        <Row label="Allocated" value={`${scopeAllocated} hrs`} />
        <Row label="Project Capacity (weekly)" value={`${projectMaxWeekly} hrs/wk`} />
        <Row label="Project Capacity (period)" value={`${projCap} hrs`} />

        {/* Per-task breakdown */}
        {tasks?.length > 0 && (
          <>
            <div className={styles.summaryPopoverDivider} />
            <div className={styles.summaryPopoverLabel} style={{ marginBottom: 4 }}>Tasks</div>
            {tasks.map((t) => {
              const isTaskOver = (t.allocated ?? 0) > projCap;
              return (
                <Row
                  key={t.id}
                  label={t.name}
                  value={t.allocation?.startDate ? `${t.allocated ?? 0} hrs` : "No dates"}
                  isOver={isTaskOver}
                  truncateLabel
                />
              );
            })}
          </>
        )}
      </>
    );
  };

  // ── Task Group ─────────────────────────────────────────────────────
  const renderTaskGroup = () => {
    const { groupAllocated, tasks, projectMaxWeekly, projCap } = data;

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div className="flex-shrink-0">{TimelineTaskIcons[group?.status]?.()}</div>
          <span className={styles.summaryPopoverTitle}>{group?.name}</span>
        </div>
        <div className={styles.summaryPopoverDivider} />

        <Row label="Allocated" value={`${groupAllocated} hrs`} />
        <Row label="Project Capacity (weekly)" value={`${projectMaxWeekly} hrs/wk`} />
        <Row label="Project Capacity (period)" value={`${projCap} hrs`} />

        {/* Per-task breakdown */}
        {tasks?.length > 0 && (
          <>
            <div className={styles.summaryPopoverDivider} />
            <div className={styles.summaryPopoverLabel} style={{ marginBottom: 4 }}>Tasks</div>
            {tasks.map((t) => {
              const isTaskOver = (t.allocated ?? 0) > projCap;
              return (
                <Row
                  key={t.id}
                  label={t.name}
                  value={t.allocation?.startDate ? `${t.allocated ?? 0} hrs` : "No dates"}
                  isOver={isTaskOver}
                  truncateLabel
                />
              );
            })}
          </>
        )}
      </>
    );
  };

  // ── Task ───────────────────────────────────────────────────────────
  const renderTask = () => {
    const { taskAllocated, projectMaxWeekly, projCap } = data;
    const alloc = task?.allocation;
    const hoursPerDay = alloc?.hoursPerDay || 0;
    const isTaskOver = taskAllocated > projCap;

    const datesLabel = (() => {
        const start = moment(alloc.startDate);
        const end = moment(alloc.endDate);
        if (start.year() === end.year()) {
          return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
        }
        return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
      })();

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div className="flex-shrink-0">{TimelineTaskIcons[task?.status]?.()}</div>
          <span className={styles.summaryPopoverTitle}>{task?.name}</span>
        </div>
        <div className={styles.summaryPopoverDivider} />

        {alloc?.startDate && (
          <Row
            label="Dates"
            value={datesLabel}
          />
        )}
        <Row label="Hours / Day" value={`${hoursPerDay} hrs`} />
        <Row label="Allocated Capacity (period)" value={`${taskAllocated} / ${projCap} hrs`} isOver={isTaskOver} />
      </>
    );
  };

  return (
    <div
      onMouseEnter={(e) => { updateCursorPosition(e); setOpen(true); }}
      onMouseMove={updateCursorPosition}
      onMouseLeave={() => setOpen(false)}
      style={{ display: "inline-flex" }}
    >
      {children}

      <Popover open={open} positioning={{ position: "after", target }} withArrow>
        <PopoverSurface className={styles.resourceSummaryPopover}>
          {renderContent()}
        </PopoverSurface>
      </Popover>
    </div>
  );
}

/** Small row helper */
function Row({ label, value, isOver, truncateLabel }) {
  return (
    <div className={styles.summaryPopoverRow}>
      <span
        className={styles.summaryPopoverLabel}
        style={truncateLabel ? { maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : undefined}
      >
        {label}
      </span>
      <span className={`${styles.summaryPopoverValue} ${isOver ? styles.summaryValueOver : ""}`}>
        {value}
      </span>
    </div>
  );
}
